import React, { useEffect, useMemo, useState, useRef } from 'react';
import {
    Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, Paper, Chip, IconButton, TablePagination, Collapse, Button,
    Divider, CircularProgress, Alert, Snackbar, Dialog, DialogTitle,
    DialogContent, DialogActions, TextField, ToggleButtonGroup, ToggleButton,
    Tooltip, Stack, LinearProgress
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import UpdateIcon from '@mui/icons-material/Update';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import RestoreIcon from '@mui/icons-material/Restore';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SaveIcon from '@mui/icons-material/Save';
import SyncIcon from '@mui/icons-material/Sync';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import ClearIcon from '@mui/icons-material/Clear';

import { DocumentItem, DocDiff } from '../types/storage';
import { eventiService, EventSuppressedItem, EventPreview } from '../services/eventiService';

/* ───────────────────────── HELPERS ───────────────────────── */
const renderValue = (v: any) => {
    if (v === null || v === undefined) return '—';
    if (Array.isArray(v)) {
        return (
            <ul style={{ margin: 0, paddingLeft: 16 }}>
                {v.map((el, i) => <li key={i}>{renderValue(el)}</li>)}
            </ul>
        );
    }
    if (typeof v === 'object') {
        return <Box component="pre" sx={{ whiteSpace: 'pre-wrap', m: 0 }}>{JSON.stringify(v, null, 2)}</Box>;
    }
    return v.toString();
};

const DiffTable: React.FC<{ diff: DocDiff }> = ({ diff }) => (
    <Box mb={2}>
        <Typography variant="subtitle2">🔍 Differenze rilevate</Typography>
        <Table size="small">
            <TableHead>
                <TableRow>
                    <TableCell><strong>Chiave</strong></TableCell>
                    <TableCell><strong>Originale</strong></TableCell>
                    <TableCell><strong>Nuovo</strong></TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {Object.entries(diff).map(([k, { oldVal, newVal }]) => (
                    <TableRow key={k}>
                        <TableCell sx={{ width: 160 }}>{k}</TableCell>
                        <TableCell sx={{ whiteSpace: 'pre-wrap' }}>{renderValue(oldVal)}</TableCell>
                        <TableCell sx={{ whiteSpace: 'pre-wrap', bgcolor: '#fff8e1' }}>{renderValue(newVal)}</TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    </Box>
);

/* ── EVENTO PREVIEW ──────────────────────────────────────── */

const CAT_COLORS: Record<string, string> = {
    'IDENTIFICAZIONE': '#e3f2fd',
    'TEMPORALE': '#e8f5e9',
    'LUOGO': '#fffde7',
    'DESTINATARI E ACCESSO': '#fce4ec',
    'CONTENUTO': '#f3e5f5',
    'PROGRAMMA': '#e0f7fa',
    'PERSONE & ORG.': '#fff3e0',
    'CONTATTI & MEDIA': '#e8eaf6',
    'METADATI': '#f5f5f5',
};

const CAT_BORDER: Record<string, string> = {
    'IDENTIFICAZIONE': '#90caf9',
    'TEMPORALE': '#a5d6a7',
    'LUOGO': '#fff176',
    'DESTINATARI E ACCESSO': '#f48fb1',
    'CONTENUTO': '#ce93d8',
    'PROGRAMMA': '#80deea',
    'PERSONE & ORG.': '#ffcc80',
    'CONTATTI & MEDIA': '#9fa8da',
    'METADATI': '#e0e0e0',
};

function Field({ label, value }: { label: string; value: any }) {
    const isEmpty =
        value === null ||
        value === undefined ||
        value === '' ||
        value === 'N/A' ||
        value === 'n/a' ||
        (Array.isArray(value) && value.length === 0);

    const displayValue = isEmpty ? 'Non definito' : value;

    return (
        <Box sx={{ display: 'flex', gap: 1, mb: 0.75, alignItems: 'flex-start' }}>
            <Typography
                variant="caption"
                sx={{ color: '#666', width: 160, flexShrink: 0, fontWeight: 600, lineHeight: 1.6, pt: '1px' }}
            >
                {label}
            </Typography>
            {Array.isArray(displayValue) ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {displayValue.map((v: any, i: number) => (
                        <Chip key={i} size="small" label={typeof v === 'object' ? JSON.stringify(v) : String(v)} sx={{ fontSize: 11 }} />
                    ))}
                </Box>
            ) : (
                <Typography
                    variant="body2"
                    sx={{
                        flex: 1,
                        whiteSpace: 'pre-wrap',
                        lineHeight: 1.6,
                        color: isEmpty ? '#aaa' : 'inherit',
                        fontStyle: isEmpty ? 'italic' : 'normal',
                    }}
                >
                    {String(displayValue)}
                </Typography>
            )}
        </Box>
    );
}

function CatCard({ title, children }: { title: string; children: React.ReactNode }) {
    return (
        <Box sx={{
            border: `1px solid ${CAT_BORDER[title] ?? '#ddd'}`,
            borderRadius: 2,
            overflow: 'hidden',
            bgcolor: '#fff',
        }}>
            <Box sx={{ bgcolor: CAT_COLORS[title] ?? '#f5f5f5', px: 2, py: 0.8, borderBottom: `1px solid ${CAT_BORDER[title] ?? '#ddd'}` }}>
                <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.5, color: '#333' }}>{title}</Typography>
            </Box>
            <Box sx={{ px: 2, py: 1.5 }}>{children}</Box>
        </Box>
    );
}

const CHUNK_TITLES: Record<string, string> = {
    identificazione: 'IDENTIFICAZIONE',
    temporale: 'TEMPORALE',
    luogo: 'LUOGO',
    target_accesso: 'DESTINATARI E ACCESSO',
    contenuto: 'CONTENUTO',
    persone_org: 'PERSONE & ORG.',
    contatti_media: 'CONTATTI & MEDIA',
    metadati: 'METADATI',
};

function ChunkCard({ title, text }: { title: string; text: string }) {
    // Unisce righe continuation (senza "Label:") alla riga precedente
    const rawLines = text.split('\n').filter(l => l.trim());
    const lines: string[] = [];
    for (const line of rawLines) {
        const hasLabel = /^[^:]{2,50}:/.test(line);
        if (hasLabel || lines.length === 0) {
            lines.push(line);
        } else {
            lines[lines.length - 1] += ' ' + line.trim();
        }
    }

    return (
        <CatCard title={title}>
            {lines.map((line, i) => {
                const colon = line.indexOf(':');
                if (colon > 0) {
                    const label = line.slice(0, colon).trim();
                    const rawValue = line.slice(colon + 1).trim();

                    if (label.toLowerCase().includes('score') || label.toLowerCase().includes('completezza')) {
                        const numMatch = rawValue.match(/(\d+)/);
                        const percent = numMatch ? parseInt(numMatch[1], 10) : 0;
                        const color = percent >= 70 ? 'success' : percent >= 40 ? 'warning' : 'error';
                        return (
                            <Box key={i} sx={{ display: 'flex', gap: 1, mb: 0.75, alignItems: 'center' }}>
                                <Typography
                                    variant="caption"
                                    sx={{ color: '#666', width: 160, flexShrink: 0, fontWeight: 600, lineHeight: 1.6 }}
                                >
                                    {label}
                                </Typography>
                                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <LinearProgress
                                        variant="determinate"
                                        value={percent}
                                        color={color}
                                        sx={{ flex: 1, height: 8, borderRadius: 4 }}
                                    />
                                    <Typography variant="caption" sx={{ minWidth: 32, color: '#555', fontWeight: 600 }}>
                                        {percent}%
                                    </Typography>
                                </Box>
                            </Box>
                        );
                    }

                    return rawValue ? <Field key={i} label={label} value={rawValue} /> : null;
                }
                return (
                    <Typography key={i} variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {line}
                    </Typography>
                );
            })}
        </CatCard>
    );
}

function EventoPreview({ docId, raw }: { docId: string; raw: string }) {
    const [preview, setPreview] = React.useState<EventPreview | null | 'loading'>('loading');

    React.useEffect(() => {
        eventiService.getEventPreview(docId).then(p => setPreview(p));
    }, [docId]);

    if (preview === 'loading') {
        return <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>;
    }

    if (preview !== null) {
        const chunks = preview.chunks || {};
        const keys = Object.keys(CHUNK_TITLES).filter(k => chunks[k as keyof typeof chunks]);
        return (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                {keys.map(k => (
                    <ChunkCard
                        key={k}
                        title={CHUNK_TITLES[k]}
                        text={chunks[k as keyof typeof chunks] as string}
                    />
                ))}
            </Box>
        );
    }

    // Fallback: JSON piatto
    let data: any;
    try { data = JSON.parse(raw); } catch {
        return <Box sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: 12 }}>{raw}</Box>;
    }

    const skip = new Set(['page_id', 'ocr_data', 'locandina']);
    const entries = Object.entries(data).filter(([k, v]) => !skip.has(k) && v !== null && v !== undefined && v !== '');
    return (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
            {entries.map(([k, v]) => (
                <Box key={k} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                    <Typography variant="caption" sx={{ color: '#666', width: 160, flexShrink: 0, fontWeight: 600 }}>{k}</Typography>
                    <Typography variant="body2" sx={{ flex: 1, whiteSpace: 'pre-wrap' }}>
                        {Array.isArray(v) ? (v as any[]).join(', ') : String(v)}
                    </Typography>
                </Box>
            ))}
        </Box>
    );
}

function jsonToText(raw: string): string {
    try {
        const obj = JSON.parse(raw);
        const ocr = obj?.ocr_data || {};
        const sections: string[] = [];
        const add = (label: string, v: any) => {
            if (!v || (Array.isArray(v) && v.length === 0)) return;
            sections.push(`${label}: ${Array.isArray(v) ? v.join(', ') : String(v)}`);
        };
        add('Titolo', obj?.titolo);
        add('Descrizione', (ocr.contenuto?.descrizione_estesa || obj?.intro));
        return sections.join('\n') || JSON.stringify(obj, null, 2);
    } catch { return raw; }
}

/* ──────────────────────── MAIN PAGE ──────────────────────── */
type ViewMode = 'attivi' | 'modificati' | 'rimossi';
const FAKE_DURATION_MS = 90_000;

const EventiPage: React.FC = () => {
    const [view, setView] = useState<ViewMode>('attivi');
    const [documenti, setDocumenti] = useState<DocumentItem[]>([]);
    const [rimossi, setRimossi] = useState<EventSuppressedItem[]>([]);
    const [modificati, setModificati] = useState<DocumentItem[]>([]);
    const [filtroNome, setFiltroNome] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [dettagliAperti, setDettagliAperti] = useState<string | null>(null);
    const [testoLetto, setTestoLetto] = useState<Record<string, string>>({});
    const [diffData, setDiffData] = useState<Record<string, DocDiff>>({});
    const [loadingId, setLoadingId] = useState<string | null>(null);
    const [errMsg, setErrMsg] = useState<Record<string, string>>({});
    const [busy, setBusy] = useState(false);
    const [snack, setSnack] = useState<{ open: boolean; msg: string; severity: 'success' | 'error' | 'info' }>({ open: false, msg: '', severity: 'success' });
    const [askScrape, setAskScrape] = useState(false);
    const [askRescrapeAll, setAskRescrapeAll] = useState(false);
    const [rescrapingIds, setRescrapingIds] = useState<Set<string>>(new Set());
    const [askRemove, setAskRemove] = useState<{ open: boolean; doc?: DocumentItem }>({ open: false });
    const [removeReason, setRemoveReason] = useState('');
    const [editDlg, setEditDlg] = useState<{ open: boolean; doc?: DocumentItem; text: string; saving?: boolean }>({ open: false, text: '' });
    const [fake, setFake] = useState<{ running: boolean; start: number; duration: number; percent: number }>({ running: false, start: 0, duration: FAKE_DURATION_MS, percent: 0 });
    const timerRef = useRef<number | null>(null);

    useEffect(() => () => { if (timerRef.current) window.clearInterval(timerRef.current); }, []);

    const loadActives = async () => {
        setBusy(true);
        try { setDocumenti(await eventiService.listDocuments()); }
        catch { setSnack({ open: true, msg: 'Errore caricamento eventi', severity: 'error' }); }
        finally { setBusy(false); }
    };

    const loadRemoved = async () => {
        setBusy(true);
        try { setRimossi(await eventiService.listRemovedDocuments()); }
        catch { setSnack({ open: true, msg: 'Errore caricamento rimossi', severity: 'error' }); }
        finally { setBusy(false); }
    };

    const loadModified = async () => {
        setBusy(true);
        try { setModificati(await eventiService.listModifiedDocuments()); }
        catch { setSnack({ open: true, msg: 'Errore caricamento modificati', severity: 'error' }); }
        finally { setBusy(false); }
    };

    useEffect(() => {
        if (view === 'attivi') loadActives();
        else if (view === 'modificati') loadModified();
        else loadRemoved();
        setPage(0); setDettagliAperti(null); setTestoLetto({}); setDiffData({}); setErrMsg({});
    }, [view]); // eslint-disable-line react-hooks/exhaustive-deps

    const toggleDettagli = (doc: DocumentItem) => {
        const id = doc.doc_id;
        const nowOpen = dettagliAperti === id ? null : id;
        setDettagliAperti(nowOpen);
        if (nowOpen && doc.status === 'pending' && !diffData[id]) {
            eventiService.getDocumentDiff(id)
                .then(d => setDiffData(prev => ({ ...prev, [id]: d })))
                .catch(() => setErrMsg(prev => ({ ...prev, [id]: 'Impossibile calcolare le differenze' })));
        }
    };

    const handleToggleTesto = async (doc: DocumentItem) => {
        const id = doc.doc_id;
        if (testoLetto[id]) { setTestoLetto(prev => { const n = { ...prev }; delete n[id]; return n; }); return; }
        try {
            setLoadingId(id);
            const txt = await eventiService.getDocumentText(id);
            setTestoLetto(prev => ({ ...prev, [id]: txt }));
        } catch { setErrMsg(prev => ({ ...prev, [id]: 'Errore lettura testo' })); }
        finally { setLoadingId(null); }
    };

    const handleApprove = async (docId: string) => {
        try { await eventiService.approveDocument(docId); await loadActives(); setSnack({ open: true, msg: 'Modifiche approvate', severity: 'success' }); }
        catch { setSnack({ open: true, msg: 'Errore approvazione', severity: 'error' }); }
    };

    const handleReject = async (docId: string) => {
        if (!window.confirm('Rifiutare e cancellare la versione modificata?')) return;
        try { await eventiService.rejectDocument(docId); await loadActives(); setSnack({ open: true, msg: 'Modifica rifiutata', severity: 'success' }); }
        catch { setSnack({ open: true, msg: 'Errore rifiuto', severity: 'error' }); }
    };

    const askRemoveFor = (doc: DocumentItem) => { setRemoveReason(''); setAskRemove({ open: true, doc }); };

    const confirmRemove = async () => {
        if (!askRemove.doc) return;
        try {
            await eventiService.suppressDocument(askRemove.doc.doc_id, removeReason);
            setAskRemove({ open: false });
            setSnack({ open: true, msg: 'Evento rimosso', severity: 'success' });
            await loadActives();
        } catch { setSnack({ open: true, msg: 'Errore rimozione', severity: 'error' }); }
    };

    const handleRestore = async (docId: string) => {
        try { await eventiService.unsuppressDocument(docId); setSnack({ open: true, msg: 'Evento ripristinato', severity: 'success' }); await loadRemoved(); }
        catch { setSnack({ open: true, msg: 'Errore ripristino', severity: 'error' }); }
    };

    const handleDelete = async (docId: string) => {
        if (!window.confirm('Eliminare definitivamente?')) return;
        try { await eventiService.deleteDocument(docId); setSnack({ open: true, msg: 'Evento eliminato', severity: 'success' }); await loadModified(); }
        catch { setSnack({ open: true, msg: 'Errore eliminazione', severity: 'error' }); }
    };

    const startFakeProgress = () => {
        const start = Date.now();
        setFake({ running: true, start, duration: FAKE_DURATION_MS, percent: 0 });
        if (timerRef.current) window.clearInterval(timerRef.current);
        timerRef.current = window.setInterval(() => {
            const elapsed = Date.now() - start;
            const p = Math.min(1, elapsed / FAKE_DURATION_MS);
            setFake(prev => ({ ...prev, percent: Math.round(p * 100) }));
            if (p >= 1) {
                if (timerRef.current) window.clearInterval(timerRef.current);
                timerRef.current = null;
                setFake(prev => ({ ...prev, running: false, percent: 100 }));
                if (view === 'attivi') loadActives();
                else if (view === 'modificati') loadModified();
                else loadRemoved();
                setSnack({ open: true, msg: 'Aggiornamento completato', severity: 'success' });
            }
        }, 300);
    };

    const doScrape = async () => {
        setAskScrape(false);
        try {
            startFakeProgress();
            await eventiService.triggerScraping();
            setSnack({ open: true, msg: 'Aggiornamento eventi avviato', severity: 'info' });
        } catch {
            if (timerRef.current) window.clearInterval(timerRef.current);
            timerRef.current = null;
            setFake({ running: false, start: 0, duration: FAKE_DURATION_MS, percent: 0 });
            setSnack({ open: true, msg: 'Errore avvio aggiornamento', severity: 'error' });
        }
    };

    const handleRescrapeEvent = async (doc: DocumentItem) => {
        setRescrapingIds(prev => new Set(prev).add(doc.doc_id));
        try {
            await eventiService.rescrapeEvent(doc.doc_id, doc.filename);
            setSnack({ open: true, msg: `Riacquisizione di "${doc.filename}" avviata`, severity: 'info' });
        } catch {
            setSnack({ open: true, msg: 'Errore avvio riacquisizione', severity: 'error' });
        } finally {
            setTimeout(() => setRescrapingIds(prev => { const n = new Set(prev); n.delete(doc.doc_id); return n; }), 3000);
        }
    };

    const doRescrapeAll = async () => {
        setAskRescrapeAll(false);
        try {
            startFakeProgress();
            await eventiService.rescrapeAll();
            setSnack({ open: true, msg: 'Riacquisizione completa avviata (può richiedere diversi minuti)', severity: 'info' });
        } catch {
            if (timerRef.current) window.clearInterval(timerRef.current);
            timerRef.current = null;
            setFake({ running: false, start: 0, duration: FAKE_DURATION_MS, percent: 0 });
            setSnack({ open: true, msg: 'Errore avvio riacquisizione', severity: 'error' });
        }
    };

    const handleOpenEdit = async (doc: DocumentItem) => {
        try {
            setLoadingId(doc.doc_id);
            const raw = await eventiService.getDocumentText(doc.doc_id);
            const text = jsonToText(raw);
            setEditDlg({ open: true, doc, text });
        } catch { setSnack({ open: true, msg: 'Errore apertura editor', severity: 'error' }); }
        finally { setLoadingId(null); }
    };

    const handleEditSave = async () => {
        if (!editDlg.doc) return;
        try {
            setEditDlg(prev => ({ ...prev, saving: true }));
            await eventiService.manualSave(editDlg.doc!.doc_id, editDlg.text);
            setSnack({ open: true, msg: 'Modifica salvata', severity: 'success' });
            setEditDlg({ open: false, text: '' });
            await Promise.all([loadActives(), loadModified()]);
        } catch { setSnack({ open: true, msg: 'Errore salvataggio', severity: 'error' }); }
        finally { setEditDlg(prev => ({ ...prev, saving: false })); }
    };

    const rows = useMemo(() => {
        let out: DocumentItem[];
        if (view === 'attivi') out = documenti.filter(d => d.status !== 'removed' && d.status !== 'manual');
        else if (view === 'modificati') out = modificati;
        else out = rimossi;

        // Ricerca per nome evento. Il filtro sta qui perché la lista arriva intera
        // dall'API e la paginazione è già client-side: filtrare a monte dello slice
        // fa sì che il conteggio della paginazione segua la ricerca.
        const q = filtroNome.trim().toLowerCase();
        return q ? out.filter(d => (d.filename ?? '').toLowerCase().includes(q)) : out;
    }, [view, documenti, modificati, rimossi, filtroNome]);

    return (
        <Box p={3}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2} gap={2}>
                <Typography variant="h4">📅 Eventi</Typography>
                <Stack direction="row" gap={1} alignItems="center">
                    <TextField
                        size="small"
                        label="Cerca nome evento"
                        value={filtroNome}
                        onChange={e => { setFiltroNome(e.target.value); setPage(0); }}
                        InputProps={{
                            endAdornment: filtroNome ? (
                                <IconButton size="small" onClick={() => { setFiltroNome(''); setPage(0); }}>
                                    <ClearIcon fontSize="small" />
                                </IconButton>
                            ) : undefined,
                        }}
                        sx={{ minWidth: 220 }}
                    />
                    <ToggleButtonGroup value={view} exclusive onChange={(_, v: ViewMode) => v && setView(v)} size="small">
                        <ToggleButton value="attivi">Attivi</ToggleButton>
                        <ToggleButton value="modificati">Modificati</ToggleButton>
                        <ToggleButton value="rimossi">Rimossi</ToggleButton>
                    </ToggleButtonGroup>
                    <Tooltip title="Aggiorna eventi dell'assistente vocale">
                        <span>
                            <Button variant="outlined" startIcon={<UpdateIcon />} onClick={() => setAskScrape(true)} disabled={busy || fake.running}>
                                Aggiorna eventi
                            </Button>
                        </span>
                    </Tooltip>
                    <Tooltip title="Elimina tutti i contenuti indicizzati e ricrea tutto da zero">
                        <span>
                            <Button variant="outlined" color="error" startIcon={<DeleteSweepIcon />} onClick={() => setAskRescrapeAll(true)} disabled={busy || fake.running}>
                                Riprocessa tutto
                            </Button>
                        </span>
                    </Tooltip>
                    {fake.running && (
                        <Stack direction="row" alignItems="center" gap={1} sx={{ minWidth: 200 }}>
                            <Box sx={{ width: 160 }}><LinearProgress variant="determinate" value={fake.percent} /></Box>
                            <Typography variant="caption" sx={{ minWidth: 44, textAlign: 'right' }}>{fake.percent}%</Typography>
                        </Stack>
                    )}
                </Stack>
            </Stack>

            <Paper elevation={2} sx={{ opacity: busy ? 0.7 : 1 }}>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell><strong>Nome evento</strong></TableCell>
                                <TableCell><strong>Stato</strong></TableCell>
                                {view === 'rimossi' && <TableCell><strong>Motivo rimozione</strong></TableCell>}
                                {view === 'rimossi' && <TableCell><strong>Rimosso il</strong></TableCell>}
                                <TableCell><strong>Ultima modifica</strong></TableCell>
                                <TableCell />
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {rows
                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                .map((doc: any) => {
                                    const open = dettagliAperti === doc.doc_id;
                                    const isPending = doc.status === 'pending';
                                    const isRemoved = view === 'rimossi';
                                    const isManual = doc.status === 'manual';

                                    return (
                                        <React.Fragment key={doc.doc_id}>
                                            <TableRow hover>
                                                <TableCell>{doc.filename}</TableCell>
                                                <TableCell>
                                                    {isRemoved ? (
                                                        <Chip label="Rimosso" color="error" size="small" icon={<WarningAmberIcon />} />
                                                    ) : (
                                                        <Chip
                                                            label={isPending ? 'Da revisionare' : (isManual ? 'Modificato manualmente' : 'Indicizzato')}
                                                            color={isPending ? 'warning' : (isManual ? 'info' : 'success')}
                                                            size="small"
                                                        />
                                                    )}
                                                </TableCell>
                                                {isRemoved && <TableCell sx={{ maxWidth: 360 }}>{doc.suppressed_reason || '—'}</TableCell>}
                                                {isRemoved && (
                                                    <TableCell>
                                                        {doc.suppressed_at ? new Date(doc.suppressed_at).toLocaleString('it-IT') : '—'}
                                                    </TableCell>
                                                )}
                                                <TableCell>
                                                    {doc.updated_at ? new Date(doc.updated_at).toLocaleString('it-IT') : '—'}
                                                </TableCell>
                                                <TableCell>
                                                    <Stack direction="row" gap={1} justifyContent="flex-end">
                                                        {!isRemoved ? (
                                                            <>
                                                                <Tooltip title="Aggiorna evento all'ultima versione del sito">
                                                                    <span>
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={() => handleRescrapeEvent(doc)}
                                                                            disabled={rescrapingIds.has(doc.doc_id) || fake.running}
                                                                        >
                                                                            {rescrapingIds.has(doc.doc_id)
                                                                                ? <CircularProgress size={16} />
                                                                                : <SyncIcon fontSize="small" />}
                                                                        </IconButton>
                                                                    </span>
                                                                </Tooltip>
                                                                <Tooltip title="Modifica manuale del contenuto">
                                                                    <span>
                                                                        <IconButton size="small" onClick={() => handleOpenEdit(doc)} disabled={loadingId === doc.doc_id}>
                                                                            <SaveIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </span>
                                                                </Tooltip>
                                                                <Tooltip title="Rimuovi evento (con motivo)">
                                                                    <span>
                                                                        <IconButton size="small" color="error" onClick={() => askRemoveFor(doc)}>
                                                                            <DeleteForeverIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </span>
                                                                </Tooltip>
                                                                <Tooltip title="Dettagli / differenze / anteprima / azioni">
                                                                    <IconButton size="small" onClick={() => toggleDettagli(doc)}>
                                                                        <EditIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </>
                                                        ) : (
                                                            <Tooltip title="Ripristina evento">
                                                                <span>
                                                                    <Button size="small" variant="contained" color="success" startIcon={<RestoreIcon />} onClick={() => handleRestore(doc.doc_id)}>
                                                                        Ripristina
                                                                    </Button>
                                                                </span>
                                                            </Tooltip>
                                                        )}
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>

                                            {!isRemoved && (
                                                <TableRow>
                                                    <TableCell colSpan={6} sx={{ p: 0 }}>
                                                        <Collapse in={open} timeout="auto" unmountOnExit>
                                                            <Box m={2} p={2} border={1} borderColor="#ddd" borderRadius={2} bgcolor="#fafafa">
                                                                <Typography variant="subtitle1" gutterBottom>Dettagli</Typography>
                                                                <Divider sx={{ mb: 2 }} />
                                                                <Typography variant="body2"><strong>ID documento:</strong> {doc.doc_id}</Typography>
                                                                <Typography variant="body2" mb={2}><strong>Percorso:</strong> {doc.file_path}</Typography>

                                                                {isPending && diffData[doc.doc_id] && <DiffTable diff={diffData[doc.doc_id]} />}
                                                                {errMsg[doc.doc_id] && <Alert severity="error">{errMsg[doc.doc_id]}</Alert>}

                                                                <Box display="flex" gap={2} mt={2} flexWrap="wrap">
                                                                    {isPending && (
                                                                        <>
                                                                            <Button variant="contained" color="success" onClick={() => handleApprove(doc.doc_id)} startIcon={<RestartAltIcon />}>
                                                                                Accetta modifiche
                                                                            </Button>
                                                                            <Button variant="outlined" color="error" onClick={() => handleReject(doc.doc_id)}>
                                                                                Rifiuta
                                                                            </Button>
                                                                        </>
                                                                    )}
                                                                    <Button variant="outlined" onClick={() => handleToggleTesto(doc)}>
                                                                        {loadingId === doc.doc_id ? 'Caricamento…' : testoLetto[doc.doc_id] ? 'Chiudi anteprima' : 'Mostra anteprima'}
                                                                    </Button>
                                                                    <Button variant="outlined" startIcon={<EditIcon />} onClick={() => handleOpenEdit(doc)}>
                                                                        Modifica manuale
                                                                    </Button>
                                                                    {view === 'modificati' && (
                                                                        <Tooltip title="Elimina definitivamente (documento e contenuti indicizzati)">
                                                                            <span>
                                                                                <Button size="small" variant="outlined" color="error" startIcon={<DeleteForeverIcon />} onClick={() => handleDelete(doc.doc_id)}>
                                                                                    Elimina
                                                                                </Button>
                                                                            </span>
                                                                        </Tooltip>
                                                                    )}
                                                                </Box>

                                                                {testoLetto[doc.doc_id] && (
                                                                    <Box mt={3}>
                                                                        <Typography variant="subtitle2" gutterBottom>Contenuto evento</Typography>
                                                                        <EventoPreview docId={doc.doc_id} raw={testoLetto[doc.doc_id]} />
                                                                    </Box>
                                                                )}
                                                            </Box>
                                                        </Collapse>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </React.Fragment>
                                    );
                                })}

                            {rows.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={view === 'rimossi' ? 6 : 5} align="center">
                                        {busy ? <CircularProgress size={20} /> : 'Nessun evento'}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    component="div"
                    count={rows.length}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={e => { setRowsPerPage(+e.target.value); setPage(0); }}
                    rowsPerPageOptions={[5, 10, 25]}
                />
            </Paper>

            <Dialog open={askScrape} onClose={() => setAskScrape(false)}>
                <DialogTitle>Aggiorna eventi dell'assistente vocale</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        Vuoi avviare l'aggiornamento degli eventi dal sito del Comune?
                        L'operazione può richiedere alcuni minuti.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAskScrape(false)}>Annulla</Button>
                    <Button variant="contained" onClick={doScrape} startIcon={<UpdateIcon />}>Avvia aggiornamento</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={askRescrapeAll} onClose={() => setAskRescrapeAll(false)}>
                <DialogTitle>Riprocessa tutto da zero</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="error" fontWeight={600} mb={1}>
                        Attenzione: questa operazione elimina tutti i documenti indicizzati dalla base di conoscenza e riacquisisce tutto dal sito.
                    </Typography>
                    <Typography variant="body2">
                        L'operazione può richiedere diversi minuti. Vuoi continuare?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAskRescrapeAll(false)}>Annulla</Button>
                    <Button variant="contained" color="error" onClick={doRescrapeAll} startIcon={<DeleteSweepIcon />}>
                        Riprocessa tutto
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={askRemove.open} onClose={() => setAskRemove({ open: false })} fullWidth maxWidth="sm">
                <DialogTitle>Rimuovi evento</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        Confermi di voler rimuovere l'evento? Verrà escluso dalla ricerca e spostato in "Rimossi".
                        Puoi inserire un motivo (opzionale).
                    </Typography>
                    <TextField
                        autoFocus fullWidth multiline minRows={2}
                        label="Motivo della rimozione (opzionale)"
                        value={removeReason}
                        onChange={(e) => setRemoveReason(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAskRemove({ open: false })}>Annulla</Button>
                    <Button variant="contained" color="error" onClick={confirmRemove} startIcon={<DeleteForeverIcon />}>Rimuovi</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={editDlg.open} onClose={() => setEditDlg({ open: false, text: '' })} fullWidth maxWidth="md">
                <DialogTitle>Modifica evento</DialogTitle>
                <DialogContent dividers>
                    <TextField
                        value={editDlg.text}
                        onChange={(e) => setEditDlg(prev => ({ ...prev, text: e.target.value }))}
                        multiline minRows={20} fullWidth variant="outlined"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditDlg({ open: false, text: '' })}>Annulla</Button>
                    <Button variant="contained" startIcon={<SaveIcon />} onClick={handleEditSave} disabled={editDlg.saving}>
                        {editDlg.saving ? 'Salvataggio…' : 'Salva modifica'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snack.open}
                autoHideDuration={2800}
                onClose={() => setSnack(s => ({ ...s, open: false }))}
                message={snack.msg}
            />
        </Box>
    );
};

export default EventiPage;
